import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import type { GameContext } from '../types';

export type ThreeKit = {
  renderer: THREE.WebGLRenderer;
  composer?: EffectComposer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  root: THREE.Group;
  lowPower: boolean;
  dispose: () => void;
  pixelRatio: () => number;
  /** true when orthographic isometric */
  isometric: boolean;
};

export function createThreeKit(
  ctx: GameContext,
  opts: {
    clear?: number;
    fov?: number;
    z?: number;
    label?: string;
    fog?: number;
    fogNear?: number;
    fogFar?: number;
    /** Classic true-iso orthographic camera */
    isometric?: boolean;
    isoSize?: number;
  } = {},
): ThreeKit {
  const lowPower = ctx.quality !== 'high';
  const isometric = !!opts.isometric;
  const pixelRatio = () => {
    const dpr = window.devicePixelRatio || 1;
    if (ctx.quality === 'low') return Math.min(1, dpr);
    if (ctx.quality === 'medium') return Math.min(1.5, dpr);
    return Math.min(2, dpr);
  };

  const renderer = new THREE.WebGLRenderer({
    antialias: !lowPower,
    alpha: false,
    powerPreference: lowPower ? 'low-power' : 'high-performance',
  });
  renderer.setClearColor(opts.clear ?? 0x030806, 1);
  renderer.setPixelRatio(pixelRatio());
  ctx.mount.innerHTML = '';
  ctx.mount.appendChild(renderer.domElement);
  renderer.domElement.className = 'game-canvas';
  if (opts.label) renderer.domElement.setAttribute('aria-label', opts.label);

  const scene = new THREE.Scene();
  if (opts.fog !== undefined) {
    scene.fog = new THREE.Fog(opts.fog, opts.fogNear ?? 12, opts.fogFar ?? 55);
  }

  let camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  if (isometric) {
    const s = opts.isoSize ?? 11;
    camera = new THREE.OrthographicCamera(-s, s, s, -s, 0.1, 120);
    // classic isometric: equal elevation on X and Z
    camera.position.set(18, 18, 18);
    camera.lookAt(0, 0, 0);
  } else {
    camera = new THREE.PerspectiveCamera(opts.fov ?? 50, 1, 0.1, 200);
    camera.position.set(0, 0, opts.z ?? 42);
    camera.lookAt(0, 0, 0);
  }

  scene.add(new THREE.AmbientLight(0x2a2028, 0.6));
  const key = new THREE.DirectionalLight(0xfff5f8, 1.2);
  key.position.set(8, 18, 10);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x00f0ff, 0.5);
  rim.position.set(-10, 6, -8);
  scene.add(rim);
  const fill = new THREE.PointLight(0xff4d6d, 0.6, 70);
  fill.position.set(0, 6, 8);
  scene.add(fill);

  const root = new THREE.Group();
  scene.add(root);

  let composer: EffectComposer | undefined;
  if (!lowPower) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 1.5, 0.4, 0.1);
    composer.addPass(bloom);
  }

  return {
    renderer,
    composer,
    scene,
    camera,
    root,
    lowPower,
    pixelRatio,
    isometric,
    dispose: () => {
      renderer.dispose();
      if (renderer.domElement.parentElement) {
        renderer.domElement.parentElement.removeChild(renderer.domElement);
      }
    },
  };
}

export function resizeThree(
  kit: ThreeKit,
  w: number,
  h: number,
  zWide = 42,
  zTall = 48,
  isoSize = 11,
): void {
  const width = Math.max(1, w);
  const height = Math.max(1, h);
  kit.renderer.setSize(width, height, false);
  if (kit.composer) kit.composer.setSize(width, height);
  const aspect = width / height;

  if (kit.isometric && kit.camera instanceof THREE.OrthographicCamera) {
    const s = isoSize;
    kit.camera.left = -s * aspect;
    kit.camera.right = s * aspect;
    kit.camera.top = s;
    kit.camera.bottom = -s;
    kit.camera.updateProjectionMatrix();
  } else if (kit.camera instanceof THREE.PerspectiveCamera) {
    kit.camera.aspect = aspect;
    kit.camera.updateProjectionMatrix();
    kit.camera.position.z = aspect < 0.7 ? zTall : zWide;
  }
}

/** Smooth follow for iso camera — keeps angle fixed, pans target */
export function setIsoLookAt(
  kit: ThreeKit,
  target: THREE.Vector3,
  dist = 18,
): void {
  if (!(kit.camera instanceof THREE.OrthographicCamera) && !kit.isometric) {
    kit.camera.lookAt(target);
    return;
  }
  kit.camera.position.set(target.x + dist, target.y + dist, target.z + dist);
  kit.camera.lookAt(target);
}

export function makeStars(count: number, color = 0xff859b): THREE.Points {
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 70;
    pos[i * 3 + 1] = (Math.random() - 0.5) * 45;
    pos[i * 3 + 2] = -6 - Math.random() * 24;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const mat = new THREE.PointsMaterial({
    color,
    size: 0.12,
    transparent: true,
    opacity: 0.75,
    depthWrite: false,
    sizeAttenuation: true,
  });
  return new THREE.Points(geo, mat);
}

export function makeGlowDisc(color: number, size = 2.2, opacity = 0.35): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  return new THREE.Mesh(new THREE.CircleGeometry(size, 24), mat);
}

export function makeGridFloor(
  w: number,
  d: number,
  color = 0x0a1f16,
  line = 0x1a4a35,
): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshStandardMaterial({ color, roughness: 0.92, metalness: 0.05 }),
  );
  base.rotation.x = -Math.PI / 2;
  g.add(base);
  const grid = new THREE.GridHelper(Math.min(w, d), 24, line, line);
  grid.position.y = 0.02;
  const mats = grid.material;
  if (Array.isArray(mats)) {
    mats.forEach((m) => {
      m.transparent = true;
      m.opacity = 0.35;
    });
  } else {
    mats.transparent = true;
    mats.opacity = 0.35;
  }
  g.add(grid);
  return g;
}

export function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else if (mat) (mat as THREE.Material).dispose();
  });
}

export function renderThree(kit: ThreeKit): void {
  if (kit.composer) {
    kit.composer.render();
  } else {
    kit.renderer.render(kit.scene, kit.camera);
  }
}

export class ParticleSystem {
  points: THREE.Points;
  private pos: Float32Array;
  private vels: Float32Array;
  private life: Float32Array;
  private max: number;
  private head = 0;

  constructor(max = 2000, color = 0xff4d6d) {
    this.max = max;
    this.pos = new Float32Array(max * 3);
    this.vels = new Float32Array(max * 3);
    this.life = new Float32Array(max);
    
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    geo.setAttribute('life', new THREE.BufferAttribute(this.life, 1));
    
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(color) } },
      vertexShader: `
        attribute float life;
        varying float vLife;
        void main() {
          vLife = life;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = (18.0 * life) / -mvPosition.z;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vLife;
        void main() {
          if (vLife <= 0.0) discard;
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float alpha = (0.5 - d) * 2.0 * vLife;
          gl_FragColor = vec4(uColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    
    this.points = new THREE.Points(geo, mat);
  }

  emit(x: number, y: number, z: number, count: number, speed = 25) {
    for (let i = 0; i < count; i++) {
      const idx = this.head;
      this.head = (this.head + 1) % this.max;
      
      this.pos[idx * 3] = x;
      this.pos[idx * 3 + 1] = y;
      this.pos[idx * 3 + 2] = z;
      
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const r = Math.random() * speed;
      
      this.vels[idx * 3] = r * Math.sin(phi) * Math.cos(theta);
      this.vels[idx * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      this.vels[idx * 3 + 2] = r * Math.cos(phi);
      
      this.life[idx] = 1.0;
    }
  }

  update(dt: number) {
    let active = false;
    for (let i = 0; i < this.max; i++) {
      if (this.life[i] > 0) {
        active = true;
        this.life[i] -= dt * 1.5;
        this.pos[i * 3] += this.vels[i * 3] * dt;
        this.pos[i * 3 + 1] += this.vels[i * 3 + 1] * dt;
        this.pos[i * 3 + 2] += this.vels[i * 3 + 2] * dt;
      }
    }
    if (active) {
      this.points.geometry.attributes.position.needsUpdate = true;
      this.points.geometry.attributes.life.needsUpdate = true;
    }
  }
}
